<template>
  <div class="app-header__container">
    <AppHeader />
  </div>
  <div id="app-main" class="app-main__container">
    <AppToast />
    <NotificacaoNovosTermos v-if="showAcceptPolicyModal" class="card-aceite-politica" />

    <template v-else>
      <div class="app-content">
        <div v-if="isRouteLoading" class="loading-wrapper">
          <Loading />
        </div>
        <div v-show="!isRouteLoading">
          <router-view v-slot="{ Component }" class="app-view">
            <ErrorBoundary @error="sendEventErroGenerico()">
              <component :is="Component" />
            </ErrorBoundary>
          </router-view>
        </div>
      </div>
      <div v-if="showFooter" class="app-footer__container">
        <AppRodape />
      </div>
      <ModalAutenticacao2fa />
    </template>
  </div>
  <DevOverlay v-if="!isProd" />
  <CookieConsentBanner />
</template>

<script lang="ts">
import CookieConsentBanner from '@shared/cookie-consent/components/CookieConsentBanner/CookieConsentBanner.vue';
import { useInstalacaoPwa } from '@shared/pwa/composables/useInstalacaoPwa';
import AppRodape from '@shared/rodape/components/AppRodape/AppRodape.vue';
import { useHead } from '@vueuse/head';
import { computed, defineComponent } from 'vue';
import { useRoute } from 'vue-router';
import AppToast from '@/components/AppToast.vue';
import ErrorBoundary from '@/components/ErrorBoundary.vue';
import ModalAutenticacao2fa from '@/components/ModalAutenticacao2fa/ModalAutenticacao2fa.vue';
import useBreadcrumbs from '@/composables/useBreadcrumbs';
import useGlobalStore from '@/stores/global';
import { useMenuSeuFocoAgora } from '@/testAB/feat_seu_foco_agora/useMenuSeuFocoAgora';

import AppHeader from './components/AppHeader.vue';
import DevOverlay from './components/dev-overlay/index.vue';
import Loading from './components/Loading.vue';
import NotificacaoNovosTermos from './components/NotificacaoNovosTermos.vue';

import { acertoAcessouErroGenerico } from './modules/events/acerto';
import { authService, queryStringService, sessionService, storageService, themeService } from './services';

const TOKEN_EXPIRATION_WATCHER_TIME_INTERVAL = 1 * 60 * 1000;

export default defineComponent({
  components: {
    AppHeader,
    AppRodape,
    AppToast,
    CookieConsentBanner,
    DevOverlay,
    ErrorBoundary,
    Loading,
    ModalAutenticacao2fa,
    NotificacaoNovosTermos,
  },
  setup() {
    const globalStore = useGlobalStore();
    const isRouteLoading = computed(() => globalStore.routeLoading);
    const route = useRoute();

    const showFooter = computed(
      () => sessionService.isLoggedIn && !!route.meta.showFooter && !globalStore.footerHidden && !isRouteLoading.value
    );

    const isProd = appSettings.VUE_APP_AMBIENTE === 'prd';
    useBreadcrumbs();
    useMenuSeuFocoAgora();
    useInstalacaoPwa();

    const canonicalUrl = computed(() => `${appSettings.VUE_APP_BASE_URL}${route.path}`);

    useHead({
      link: [{ rel: 'canonical', href: canonicalUrl }],
    });

    queryStringService.merge();
    const query = queryStringService.getFromStorage();

    themeService.loadTheme(query.tema, query.parceiro === 'true');

    async function setRefreshTokenTimeout() {
      const token = storageService.session.get();

      if (token) {
        const isLessThan10MinutesToExpire = token.timeLeftToExpire < 10 * 60 * 1000;

        if (sessionService.userIsRegistered && isLessThan10MinutesToExpire && !token.isExpired) {
          await authService.renovarToken();
        }

        sessionService.reload();
      }

      setTimeout(setRefreshTokenTimeout, TOKEN_EXPIRATION_WATCHER_TIME_INTERVAL);
    }

    setRefreshTokenTimeout();

    const sendEventErroGenerico = () => {
      logger.tracker.action('exibindo tela de erro genérico não tratado');
      acertoAcessouErroGenerico();
    };

    const showAcceptPolicyModal = computed(
      () =>
        route.path !== '/logout' &&
        sessionService.isLoggedIn &&
        sessionService.userIsRegistered &&
        !!sessionService.token && // para tipagem contextual
        !sessionService.token.isBackOffice &&
        sessionService.token.pendingPoliciesToAccept.length > 0
    );

    return {
      showFooter,
      isRouteLoading,
      showAcceptPolicyModal,
      sendEventErroGenerico,
      isProd,
    };
  },
});
</script>

<style lang="scss">
@use "@/assets/styles/vars" as *;

/*
 * `min-height`, e não `height`: o documento é quem rola, então o `#app` precisa poder crescer além
 * da tela. O `dvh` acompanha a barra de endereço do mobile recolhendo e expandindo.
 */
#app {
  /* `vh` antes, para navegador sem `dvh` (iOS anterior ao 15.4) não descartar a regra inteira. */
  min-height: 100vh;
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
}

/*
 * Já foi o scroller do app. Hoje é só o container do conteúdo — o `id` continua porque três toasts
 * se teleportam para ele.
 */
.app-main__container {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
}

.app-content {
  flex: 1 0 auto;

  /*
   * Deixa o rodapé abaixo da dobra em página curta: o miolo sozinho já ocupa a tela menos o header.
   * Sem isto o `flex-grow` para no rodapé em vez de passar por ele, e o "Sobre a Acerto" nasce
   * visível. Percentual não resolve aqui — o `#app` tem `min-height`, e não `height`.
   */
  min-height: calc(100vh - #{$altura-header-mobile});
  min-height: calc(100dvh - #{$altura-header-mobile});

  @include responsive($desktop) {
    min-height: calc(100vh - #{$altura-header-desktop});
    min-height: calc(100dvh - #{$altura-header-desktop});
  }
}

.loading-wrapper {
  margin: 64px auto;
  width: fit-content;
}

.app-view {
  flex: 1;
}
</style>
